import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { AlertCircleIcon, CheckCircleIcon, ChevronDownIcon, ArrowRightIcon, ZapIcon, BarChart2Icon, MessageSquareIcon, RadioIcon, UsersIcon, LayoutDashboardIcon, StarIcon } from 'lucide-react';
function FadeInSection({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 32
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} data-id="element-299">
      {children}
    </motion.div>;
}
const controlPillars = [{
  letter: 'C',
  title: 'Campaña propia',
  description: 'Tu motor de generación de leads. Sin depender de nadie.',
  icon: RadioIcon
}, {
  letter: 'O',
  title: 'Optimización',
  description: 'Datos reales para mejorar cada semana.',
  icon: BarChart2Icon
}, {
  letter: 'N',
  title: 'Nutrición',
  description: 'Secuencias automáticas que mantienen el contacto.',
  icon: MessageSquareIcon
}, {
  letter: 'T',
  title: 'Tráfico',
  description: 'Canales pagados y orgánicos bajo tu control.',
  icon: ZapIcon
}, {
  letter: 'R',
  title: 'Reclutamiento',
  description: 'Sistema para crecer tu equipo con estructura.',
  icon: UsersIcon
}, {
  letter: 'O',
  title: 'Operación',
  description: 'CRM, dashboards y procesos documentados.',
  icon: LayoutDashboardIcon
}, {
  letter: 'L',
  title: 'Liderazgo',
  description: 'Mentoría y comunidad de agentes que ya lo están construyendo.',
  icon: StarIcon
}];
const includes = ['Campaña publicitaria configurada y activa', 'CRM con automatizaciones instaladas', 'Scripts de conversación y manejo de objeciones', 'Sistema de reclutamiento con proceso documentado', 'Dashboard de métricas operativas', 'Sesiones de revisión y optimización continua', 'Comunidad privada de agentes en el programa', 'Soporte directo por WhatsApp'];
const faqs = [{
  q: '¿Necesito experiencia en marketing digital?',
  a: 'No. Nosotros configuramos todo. Tu trabajo es ejecutar el proceso que instalamos juntos.'
}, {
  q: '¿Esto reemplaza a mi agencia o upline?',
  a: 'No. Complementa tu estructura actual añadiendo un sistema propio de generación y conversión.'
}, {
  q: '¿En cuánto tiempo veo resultados operativos?',
  a: 'Depende de tu mercado y ejecución. El sistema está diseñado para generar actividad estructurada desde las primeras semanas.'
}, {
  q: '¿Qué pasa si ya tengo leads o una campaña activa?',
  a: 'Perfecto. Auditamos lo que tienes y optimizamos desde ahí.'
}, {
  q: '¿Hay garantía de resultados?',
  a: 'No garantizamos ingresos ni resultados específicos. Garantizamos que el sistema queda instalado y funcionando.'
}];
function FAQItem({
  q,
  a
}: {
  q: string;
  a: string;
}) {
  const [open, setOpen] = useState(false);
  return <div className="border border-border-subtle rounded-xl overflow-hidden" data-id="element-300">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-surface transition-colors" aria-expanded={open} data-id="element-301">
        <span className="font-medium text-white text-sm sm:text-base pr-4" data-id="element-302">
          {q}
        </span>
        <motion.div animate={{
        rotate: open ? 180 : 0
      }} transition={{
        duration: 0.2
      }} data-id="element-303">
          <ChevronDownIcon size={18} className="text-[#a0a0a0] flex-shrink-0" data-id="element-304" />
        </motion.div>
      </button>
      <motion.div initial={false} animate={{
      height: open ? 'auto' : 0,
      opacity: open ? 1 : 0
    }} transition={{
      duration: 0.25,
      ease: 'easeInOut'
    }} className="overflow-hidden" data-id="element-305">
        <div className="px-5 pb-4 text-sm text-[#a0a0a0] leading-relaxed border-t border-border-subtle pt-3" data-id="element-306">
          {a}
        </div>
      </motion.div>
    </div>;
}
export function AgentesPage() {
  return <div className="bg-charcoal" data-id="element-307">
      {/* HERO */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8" data-id="element-308">
        <div className="max-w-4xl mx-auto text-center" data-id="element-309">
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-310">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-xs font-semibold tracking-wide uppercase mb-6" data-id="element-311">
              Para Agentes de Seguros
            </span>
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-cream leading-tight tracking-tight mb-6" data-id="element-312">
            Un agente sin sistema no tiene un negocio.{' '}
            <span className="text-accent" data-id="element-313">Tiene incertidumbre.</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="text-lg sm:text-xl text-[#b8b4ae] mb-10 max-w-2xl mx-auto leading-relaxed" data-id="element-314">
            Instala CONTROL: leads propios + proceso de ventas + reclutamiento
            con estructura.
          </motion.p>
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-315">
            <a href="/contact-us" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent hover:bg-accent-hover text-charcoal font-semibold rounded-xl transition-colors duration-200" data-id="element-316">
              Agendar diagnóstico
              <ArrowRightIcon size={16} data-id="element-317" />
            </a>
            <Link to="/como-funciona" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border-subtle text-cream hover:border-accent/50 hover:text-accent rounded-xl transition-colors duration-200 font-medium" data-id="element-318">
              Ver cómo funciona
            </Link>
          </motion.div>
        </div>
      </section>

      {/* PROBLEMA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 border-t border-border-subtle" data-id="element-319">
        <div className="max-w-5xl mx-auto" data-id="element-320">
          <FadeInSection data-id="element-321">
            <h2 className="text-2xl sm:text-3xl font-bold text-cream text-center mb-12" data-id="element-322">
              ¿Te identificas con esto?
            </h2>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-id="element-323">
            <FadeInSection delay={0.1} data-id="element-324">
              <div className="bg-surface border border-border-subtle border-l-4 border-l-red-500/60 rounded-xl p-6" data-id="element-325">
                <AlertCircleIcon size={22} className="text-red-400 mb-3" data-id="element-326" />
                <h3 className="font-semibold text-cream mb-2" data-id="element-327">
                  Dependencia de referidos
                </h3>
                <p className="text-sm text-[#b8b4ae] leading-relaxed" data-id="element-328">
                  Dependes de referidos que no siempre llegan. Tu pipeline es
                  impredecible y fuera de tu control.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-329">
              <div className="bg-surface border border-border-subtle border-l-4 border-l-red-500/60 rounded-xl p-6" data-id="element-330">
                <AlertCircleIcon size={22} className="text-red-400 mb-3" data-id="element-331" />
                <h3 className="font-semibold text-cream mb-2" data-id="element-332">
                  Leads que no convierten
                </h3>
                <p className="text-sm text-[#b8b4ae] leading-relaxed" data-id="element-333">
                  Compraste leads y no convirtieron como esperabas. Dinero
                  invertido sin sistema de seguimiento.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.2} data-id="element-334">
              <div className="bg-surface border border-border-subtle border-l-4 border-l-red-500/60 rounded-xl p-6" data-id="element-335">
                <AlertCircleIcon size={22} className="text-red-400 mb-3" data-id="element-336" />
                <h3 className="font-semibold text-cream mb-2" data-id="element-337">
                  Sin proceso repetible
                </h3>
                <p className="text-[#b8b4ae] text-sm leading-relaxed" data-id="element-338">
                  No tienes un proceso repetible — cada semana empieza de cero.
                  La improvisación es el enemigo del crecimiento.
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* QUÉ ES CONTROL */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-339">
        <div className="max-w-6xl mx-auto" data-id="element-340">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-341">
            <FadeInSection data-id="element-342">
              <div data-id="element-343">
                <span className="text-accent text-xs font-semibold uppercase tracking-widest mb-3 block" data-id="element-344">
                  El sistema
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-5 leading-tight" data-id="element-345">
                  CONTROL no es un curso. Es un sistema operativo.
                </h2>
                <p className="text-[#b8b4ae] leading-relaxed mb-6" data-id="element-346">
                  Instalamos las palancas que convierten tu actividad en
                  resultados predecibles. Sin improvisación. Sin depender de
                  leads prestados.
                </p>
                <p className="text-[#b8b4ae] leading-relaxed" data-id="element-347">
                  Cada componente del sistema está diseñado para funcionar en
                  conjunto: desde la generación del lead hasta el reclutamiento
                  de tu equipo.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-348">
              {/* System Diagram */}
              <div className="bg-elevated border border-border-subtle rounded-2xl p-6" data-id="element-349">
                <p className="text-xs text-[#7a7672] uppercase tracking-widest mb-5 font-medium" data-id="element-350">
                  Flujo del sistema
                </p>
                <div className="flex flex-col gap-2" data-id="element-351">
                  {[{
                  label: 'Campaña',
                  color: 'bg-accent/20 border-accent/40 text-accent'
                }, {
                  label: 'Lead Propio',
                  color: 'bg-accent/20 border-accent/40 text-accent'
                }, {
                  label: 'Conversación',
                  color: 'bg-elevated border-border-subtle text-cream'
                }, {
                  label: 'Cita',
                  color: 'bg-elevated border-border-subtle text-cream'
                }, {
                  label: 'Póliza',
                  color: 'bg-accent/10 border-accent/30 text-accent'
                }, {
                  label: 'Reclutamiento',
                  color: 'bg-accent/10 border-accent/30 text-accent'
                }].map((step, i) => <div key={step.label} className="flex flex-col items-center" data-id="element-352">
                      <div className={`w-full border rounded-lg px-4 py-2.5 text-sm font-medium text-center ${step.color}`} data-id="element-353">
                        {step.label}
                      </div>
                      {i < 5 && <div className="w-px h-4 bg-border-subtle" data-id="element-354" />}
                    </div>)}
                </div>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* MÉTODO CONTROL */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-355">
        <div className="max-w-6xl mx-auto" data-id="element-356">
          <FadeInSection data-id="element-357">
            <div className="text-center mb-12" data-id="element-358">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-359">
                Las 7 palancas del método CONTROL
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-360">
                Cada letra. Un sistema. Un resultado.
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-id="element-361">
            {controlPillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return <FadeInSection key={pillar.letter + pillar.title} delay={i * 0.06} data-id="element-362">
                  <div className="bg-surface border border-border-subtle rounded-xl p-5 h-full" data-id="element-363">
                    <div className="flex items-start gap-3 mb-3" data-id="element-364">
                      <span className="text-3xl font-black text-accent leading-none" data-id="element-365">
                        {pillar.letter}
                      </span>
                      <Icon size={18} className="text-[#7a7672] mt-1.5 flex-shrink-0" data-id="element-366" />
                    </div>
                    <h3 className="font-semibold text-cream mb-1.5 text-sm" data-id="element-367">
                      {pillar.title}
                    </h3>
                    <p className="text-xs text-[#b8b4ae] leading-relaxed" data-id="element-368">
                      {pillar.description}
                    </p>
                  </div>
                </FadeInSection>;
          })}
          </div>
        </div>
      </section>

      {/* QUÉ INCLUYE */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-369">
        <div className="max-w-4xl mx-auto" data-id="element-370">
          <FadeInSection data-id="element-371">
            <div className="text-center mb-12" data-id="element-372">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-373">
                6 meses de acompañamiento estructurado
              </h2>
              <p className="text-[#b8b4ae]" data-id="element-374">
                Todo lo que necesitas para instalar el sistema completo.
              </p>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-375">
            {includes.map((item, i) => <FadeInSection key={item} delay={i * 0.07} data-id="element-376">
                <div className="flex items-start gap-3 bg-elevated border border-border-subtle rounded-xl px-5 py-4" data-id="element-377">
                  <CheckCircleIcon size={18} className="text-accent flex-shrink-0 mt-0.5" data-id="element-378" />
                  <span className="text-sm text-cream font-medium" data-id="element-379">{item}</span>
                </div>
              </FadeInSection>)}
          </div>
        </div>
      </section>

      {/* PROCESO */}
      <section className="py-20 px-4 sm:px-6 lg:px-8" data-id="element-380">
        <div className="max-w-5xl mx-auto" data-id="element-381">
          <FadeInSection data-id="element-382">
            <div className="text-center mb-12" data-id="element-383">
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-3" data-id="element-384">
                El proceso en 2 fases
              </h2>
            </div>
          </FadeInSection>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-385">
            <FadeInSection delay={0.1} data-id="element-386">
              <div className="bg-surface border border-border-subtle rounded-2xl p-8 h-full" data-id="element-387">
                <div className="flex items-center gap-3 mb-5" data-id="element-388">
                  <span className="w-8 h-8 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-accent font-bold text-sm" data-id="element-389">
                    1
                  </span>
                  <span className="text-xs text-[#7a7672] uppercase tracking-widest font-medium" data-id="element-390">
                    Fase 1
                  </span>
                </div>
                <h3 className="text-xl font-bold text-cream mb-3" data-id="element-391">
                  Campaña activa
                </h3>
                <p className="text-[#b8b4ae] leading-relaxed" data-id="element-392">
                  Configuramos tu campaña publicitaria, conectamos tu CRM y
                  activamos el flujo de leads propios. Tu sistema empieza a
                  generar actividad desde el día uno.
                </p>
              </div>
            </FadeInSection>
            <FadeInSection delay={0.15} data-id="element-393">
              <div className="bg-surface border border-border-subtle rounded-2xl p-8 h-full" data-id="element-394">
                <div className="flex items-center gap-3 mb-5" data-id="element-395">
                  <span className="w-8 h-8 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center text-accent font-bold text-sm" data-id="element-396">
                    2
                  </span>
                  <span className="text-xs text-[#7a7672] uppercase tracking-widest font-medium" data-id="element-397">
                    Fase 2
                  </span>
                </div>
                <h3 className="text-xl font-bold text-cream mb-3" data-id="element-398">
                  Ventas + Reclutamiento
                </h3>
                <p className="text-[#b8b4ae] leading-relaxed" data-id="element-399">
                  Con leads entrando, instalamos el proceso de conversión y el
                  sistema de reclutamiento paralelo. Escalas con estructura, no
                  con improvisación.
                </p>
              </div>
            </FadeInSection>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface" data-id="element-400">
        <div className="max-w-3xl mx-auto" data-id="element-401">
          <FadeInSection data-id="element-402">
            <h2 className="text-2xl sm:text-3xl font-bold text-cream text-center mb-10" data-id="element-403">
              Preguntas frecuentes
            </h2>
          </FadeInSection>
          <div className="space-y-3" data-id="element-404">
            {faqs.map((faq, i) => <FadeInSection key={faq.q} delay={i * 0.06} data-id="element-405">
                <FAQItem q={faq.q} a={faq.a} data-id="element-406" />
              </FadeInSection>)}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-id="element-407">
        <div className="max-w-3xl mx-auto text-center" data-id="element-408">
          <FadeInSection data-id="element-409">
            <div className="bg-surface border border-accent/20 rounded-3xl p-10 sm:p-14" data-id="element-410">
              <span className="inline-block w-12 h-1 bg-accent rounded-full mb-6" data-id="element-411" />
              <h2 className="text-2xl sm:text-3xl font-bold text-cream mb-4" data-id="element-412">
                ¿Listo para instalar tu sistema?
              </h2>
              <p className="text-[#b8b4ae] mb-8 leading-relaxed" data-id="element-413">
                Agenda un diagnóstico gratuito. Sin compromiso. Sin ventas de
                alta presión.
              </p>
              <a href="/contact-us" className="inline-flex items-center gap-2 px-8 py-4 bg-accent hover:bg-accent-hover text-charcoal font-bold rounded-xl transition-colors duration-200 text-base" data-id="element-414">
                Agendar diagnóstico
                <ArrowRightIcon size={18} data-id="element-415" />
              </a>
            </div>
          </FadeInSection>
        </div>
      </section>
    </div>;
}